import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import OpportunityDto from '@/models/Opportunities/OpportunityDto';
import OpportunityStatus from '@/models/Opportunities/OpportunityStatus';

// Broader filter applied via the OpportunityListSearchCriteria dialog (mirrors
// the Jobs / Contacts search-criteria pattern). Stage is NOT here — it stays as
// the separate button row on the list view. Status (Open/Closed) lives here,
// defaulting to Open, like the Job Search Filter. Everything is server-side so
// the stage button counts reflect the active filter.
export interface IOpportunityListFilter {
  status: number;
  branchId: string | null;
  deskId: string | null;
  ownerId: string | null;
  workTypes: string[];
  locationTypeIds: string[];
}

export function emptyOpportunityListFilter(): IOpportunityListFilter {
  return {
    status: OpportunityStatus.Open,
    branchId: null,
    deskId: null,
    ownerId: null,
    workTypes: [],
    locationTypeIds: [],
  };
}

export interface IOpportunitiesListState {
  loading: boolean;
  opportunities: OpportunityDto[];
  filter: IOpportunityListFilter;
}

@Module({ dynamic: true, store, name: 'OpportunitiesList', namespaced: true })
class OpportunitiesList extends VuexModule implements IOpportunitiesListState {
  public loading = false;
  public opportunities: OpportunityDto[] = [];
  public filter: IOpportunityListFilter = emptyOpportunityListFilter();

  @Mutation
  setLoading(v: boolean) { this.loading = v; }

  @Mutation
  setOpportunities(items: OpportunityDto[]) { this.opportunities = items; }

  @Mutation
  setFilter(filter: IOpportunityListFilter) { this.filter = filter; }

  // True when any criteria differ from the defaults — drives the filter
  // button's "active" colour. Status counts only when set to Closed (Open is
  // the default view).
  get hasActiveFilters(): boolean {
    const f = this.filter;
    return f.status !== OpportunityStatus.Open
      || !!f.branchId || !!f.deskId || !!f.ownerId
      || (f.workTypes && f.workTypes.length > 0)
      || (f.locationTypeIds && f.locationTypeIds.length > 0);
  }

  // Called by the search-criteria dialog's Search button. The dialog mutates
  // this.filter directly (same pattern as Jobs/Contacts), so we just refetch.
  @Action
  async search() {
    await this.fetch();
  }

  @Action
  async clearFilter() {
    this.setFilter(emptyOpportunityListFilter());
    await this.fetch();
  }

  @Action
  async fetch() {
    this.setLoading(true);
    try {
      const f = this.filter;
      const params = new URLSearchParams();
      params.append('status', String(f.status));
      if (f.branchId) params.append('branchId', f.branchId);
      if (f.deskId) params.append('deskId', f.deskId);
      if (f.ownerId) params.append('ownerId', f.ownerId);
      (f.workTypes || []).forEach(wt => params.append('workTypes', wt));
      (f.locationTypeIds || []).forEach(id => params.append('locationTypeId', id));
      const response = await ajax.get(`/api/v1/agency/opportunities?${params.toString()}`);
      this.setOpportunities(response.data || []);
    } catch (error) {
      AppModule.handleApiError(error);
      this.setOpportunities([]);
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  cleanState() {
    this.context.commit('resetState');
  }

  @Mutation
  resetState() {
    this.loading = false;
    this.opportunities = [];
    this.filter = emptyOpportunityListFilter();
  }
}

export const OpportunitiesListModule = getModule(OpportunitiesList);
